import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Playlist } from "../../store/slices/types";

interface PlaylistFormProps {
  playlist?: Playlist | null;
  onSave: (playlistData: Partial<Playlist>) => void;
  onCancel: () => void;
  saving?: boolean;
}

const PlaylistForm: React.FC<PlaylistFormProps> = ({
  playlist,
  onSave,
  onCancel,
  saving = false,
}) => {
  const [formData, setFormData] = useState({
    title: playlist?.title || "",
    description: playlist?.description || "",
    userId: playlist?.userId || "",
    items: playlist?.items?.join(", ") || "",
    tags: playlist?.tags?.join(", ") || "",
    isPublic: playlist?.isPublic !== undefined ? playlist.isPublic : true,
    color: playlist?.color || "#667eea",
  });

  useEffect(() => {
    setFormData({
      title: playlist?.title || "",
      description: playlist?.description || "",
      userId: playlist?.userId || "",
      items: playlist?.items?.join(", ") || "",
      tags: playlist?.tags?.join(", ") || "",
      isPublic: playlist?.isPublic !== undefined ? playlist.isPublic : true,
      color: playlist?.color || "#667eea",
    });
    setErrors({});
  }, [playlist]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Playlist title is required";
    } else if (formData.title.trim().length < 3) {
      newErrors.title = "Playlist title must be at least 3 characters";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    if (!formData.userId.trim()) {
      newErrors.userId = "User ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const playlistData = {
        ...formData,
        items: formData.items
          .split(",")
          .map((item) => item.trim())
          .filter((item) => item),
        tags: formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
      };
      onSave(playlistData);
    }
  };

  return (
    <Box>
      {/* Basic Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📝 Basic Information
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Playlist Title'
              placeholder='Enter a descriptive title for your playlist'
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (errors.title) setErrors({ ...errors, title: "" });
              }}
              error={!!errors.title}
              helperText={errors.title}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label='Description'
              placeholder='Provide a detailed description of this playlist'
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                if (errors.description)
                  setErrors({ ...errors, description: "" });
              }}
              error={!!errors.description}
              helperText={errors.description}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 140,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='User ID'
              placeholder='Enter the user ID who owns this playlist'
              value={formData.userId}
              onChange={(e) => {
                setFormData({ ...formData, userId: e.target.value });
                if (errors.userId) setErrors({ ...errors, userId: "" });
              }}
              error={!!errors.userId}
              helperText={errors.userId || "The user who created this playlist"}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Color'
              placeholder='#667eea'
              value={formData.color}
              onChange={(e) =>
                setFormData({ ...formData, color: e.target.value })
              }
              helperText='Hex color code for the playlist theme'
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    🎨
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Content Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          🎵 Playlist Content
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label='Items (Content IDs)'
              placeholder='Enter content IDs separated by commas (e.g., id1, id2, id3)'
              value={formData.items}
              onChange={(e) =>
                setFormData({ ...formData, items: e.target.value })
              }
              helperText='Enter content IDs (modules, resources, etc.) separated by commas'
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 120,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Tags'
              placeholder='wellness, meditation, stress-relief'
              value={formData.tags}
              onChange={(e) =>
                setFormData({ ...formData, tags: e.target.value })
              }
              helperText='Separate tags with commas to help users find this playlist'
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    🏷️
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Privacy Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          🔒 Privacy Settings
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPublic}
                  onChange={(e) =>
                    setFormData({ ...formData, isPublic: e.target.checked })
                  }
                />
              }
              label={
                formData.isPublic
                  ? "🌐 Public - Visible to all users"
                  : "🔒 Private - Only visible to the creator"
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        <Button
          onClick={onCancel}
          variant='outlined'
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={saving}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
            },
          }}
        >
          {saving
            ? "Saving..."
            : playlist
            ? "Update Playlist"
            : "Create Playlist"}
        </Button>
      </Box>
    </Box>
  );
};

export default PlaylistForm;
